import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { logOut } from '../../services/authService';
import { ROUTES } from '../../utils/constants';
import { isAdminRole } from '../../utils/topChromeActions';
import { isOperatorShellRole } from '../../utils/sessionRole';
import { canAccessTraining } from '../../utils/trainingAccess';
import { canManagePricing } from '../../pages/Admin/pricingAccess';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { useSupportAssignments } from '../../pages/Admin/useSupportAssignments';
import DealerWorkspaceLink from './DealerWorkspaceLink';
import { useState, useEffect } from 'react';
import NotificationCenter from '../notifications/NotificationCenter';
import NotificationDebugConsole from '../debug/NotificationDebugConsole';
import { useHelpOptional } from '../../help/HelpContext';
const signalBridgeLogo = '/app-icon.png';

const HelpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8.228 9c.549-1.165 1.76-2 3.272-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const Header = ({ minimal = false, hideHelp = false }) => {
  const { currentUser, isAdmin, role, userClaims, userData } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const supportAssignments = useSupportAssignments(currentUser?.uid, dealerAdmin.active);
  const navigate = useNavigate();
  const help = useHelpOptional();
  const [showDebugConsole, setShowDebugConsole] = useState(false);

  // Keyboard shortcut: Ctrl+Shift+D opens debug console (admin only)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        if (isAdmin) setShowDebugConsole(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAdmin]);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const effectiveRole = role || userClaims?.role || userData?.role;
  const showPricingLink = canManagePricing({ role: effectiveRole, permissions: userData?.permissions }) && !dealerAdmin.active;
  const showTrainingLink = canAccessTraining(effectiveRole);
  const showOperatorHomeLink = isOperatorShellRole(effectiveRole) || (!isAdmin && !isAdminRole(effectiveRole));
  const brandPath = showOperatorHomeLink ? ROUTES.HOME_LAUNCHER : ROUTES.DASHBOARD;

  if (!currentUser) return null;

  return (
    <header className="bg-white shadow z-50">
      <div className="w-full px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
        {/* Left side - Logo */}
        <Link to={brandPath} className="flex items-center space-x-2">
          <img
            src={signalBridgeLogo}
            alt="Signal Bridge logo"
            className="h-8 w-8 shrink-0 bg-transparent object-contain"
          />
          <span className="text-2xl font-bold text-brand-navy">Signal Bridge</span>
        </Link>

        {/* Right side - Tenant selector and icons */}
        <div className="flex items-center space-x-6">
          {currentUser && (
            <>
              {/* Notification Center */}
              {!minimal && (
                <div className="flex items-center">
                  <NotificationCenter 
                    onNotificationClick={(notification) => {
                      // Critical notifications will trigger modal via store
                      console.log('[HEADER] Notification clicked:', notification.id);
                    }}
                  />
                </div>
              )}

              {/* Page Help — kept in the header when the admin sidebar is not shown */}
              {help && !hideHelp && (
                <button
                  type="button"
                  onClick={help.toggleHelp}
                  className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    help.isOpen
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                  title="Page Help"
                  aria-label="Open page help"
                  aria-pressed={help.isOpen}
                >
                  <HelpIcon />
                  <span className="hidden sm:inline">Help</span>
                </button>
              )}

              {showOperatorHomeLink ? (
                <Link
                  to={ROUTES.HOME_LAUNCHER}
                  className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Home"
                  aria-label="Home"
                >
                  <span aria-hidden="true">🏠</span>
                  <span className="hidden sm:inline">Home</span>
                </Link>
              ) : null}

              {supportAssignments.length > 0 && !dealerAdmin.active ? (
                <Link
                  to={ROUTES.SUPPORTED_TENANTS}
                  className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Supported Tenants"
                  aria-label="Supported Tenants"
                >
                  <span>Supported Tenants</span>
                </Link>
              ) : null}

              <Link
                to={ROUTES.SUPPORT_REPORTS}
                className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Support"
                aria-label="Support"
              >
                <span aria-hidden="true">📋</span>
                <span className="hidden sm:inline">Support</span>
              </Link>

              {currentUser && !dealerAdmin.active ? (
                <Link
                  to={ROUTES.SUBSCRIPTION}
                  className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Subscription"
                  aria-label="Subscription"
                >
                  <span>Subscription</span>
                </Link>
              ) : null}

              <DealerWorkspaceLink variant="header" />

              {showPricingLink ? (
                <Link
                  to={ROUTES.ADMIN_PRICING}
                  className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Pricing"
                  aria-label="Pricing"
                >
                  <span className="hidden sm:inline">Pricing</span>
                </Link>
              ) : null}

              {showTrainingLink ? (
                <Link
                  to={ROUTES.TRAINING}
                  className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Training"
                  aria-label="Training"
                >
                  <span aria-hidden="true">🎓</span>
                  <span className="hidden sm:inline">Training</span>
                </Link>
              ) : null}

              {/* User Profile and Logout */}
              <div className="flex items-center ml-4 border-l pl-4">
                {currentUser.photoURL && (
                  <img
                    src={currentUser.photoURL}
                    alt={currentUser.displayName || 'User'}
                    className="w-8 h-8 rounded-full"
                  />
                )}
                <button
                  onClick={handleLogout}
                  className="ml-3 text-gray-700 hover:text-red-600 flex items-center"
                  title="Sign Out"
                >
                  <span className="text-xl">🔓</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <NotificationDebugConsole open={showDebugConsole} onClose={() => setShowDebugConsole(false)} />
    </header>
  );
};

export default Header;
